<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import useAuthStore, { isPreAuthOnboardingToken, isUserToken, type AdaJwtClaims } from '@/stores/authStore'
import mdiAccountSwitchOutline from '~icons/mdi/account-switch-outline'
import mdiChevronDown from '~icons/mdi/chevron-down'
import { useAccess } from '@/composables/useAccess'
import { useAuth } from '@/composables/useAuth'
import { useQuery } from '@/composables/api'
import { textInitials, textToBackgroundColor } from '@/utils/textConversions'
import { getIntlLocales } from '@/utils/format'

const { t } = useI18n()
const auth = useAuth()
const authStore = useAuthStore()
const router = useRouter()
const { canAccessResource } = useAccess()

// Resolve the onboarding's account name so the token shows "Onboarding för: X" for a linked account too
// (the token itself only carries onb_id/u_name). account_name is denormalized for both branches.
const onboardingTokenPayload = computed(() =>
  authStore.tokensList.map((item) => item.payload).find(isPreAuthOnboardingToken)
)
// Only Business Operations grants onboardings access, so anyone else holding an
// onboarding token in their list would 403 on every page load without this guard.
const canAccessOnboardings = canAccessResource('onboardings')
const onboardingQuery = useQuery(
  '/onboardings/{resource_id}',
  computed(() => ({ path: { resource_id: onboardingTokenPayload.value?.onb_id ?? '' } })),
  {
    enabled: () => onboardingTokenPayload.value != null && canAccessOnboardings.value,
    meta: { notifyOnError: false },
  }
)
const onboardingAccountName = computed(() => onboardingQuery.data.value?.account_name ?? '')

function switchTo(item: (typeof sortedTokens.value)[number]) {
  const wasPreAuthOnboarding = isPreAuthOnboardingToken(auth.selectedToken.value?.payload)
  authStore.selectToken(item.token)
  if (isPreAuthOnboardingToken(item.payload)) {
    // No route param — useCustomerOnboarding reads onb_id from the selected token.
    void router.push({ name: 'CustomerOnboarding' })
  } else if (wasPreAuthOnboarding && isUserToken(item.payload)) {
    void router.push({ name: 'Dashboard' })
  }
}

function tokenTitle(payload?: AdaJwtClaims | null) {
  if (isPreAuthOnboardingToken(payload)) return payload.u_name ?? ''
  if (!isUserToken(payload)) return ''

  return (payload.u_name as string | undefined) ?? ''
}

function tokenSubtitle(payload?: AdaJwtClaims | null) {
  if (isPreAuthOnboardingToken(payload)) {
    const accountName = payload.onb_id === onboardingTokenPayload.value?.onb_id ? onboardingAccountName.value : ''
    return accountName !== '' ? t('common.onboarding_for_x', { x: accountName }) : t('common.onboarding')
  }
  if (!isUserToken(payload)) return ''

  if (payload.system_user === true) return t('common.system_user')
  else if (payload.solicitor === true) return t('common.solicitor')
  else return payload.u_org
}

function tokenAvatarText(payload?: AdaJwtClaims | null) {
  if (isPreAuthOnboardingToken(payload)) return payload.u_name ?? ''
  if (!isUserToken(payload)) return ''

  if (payload.system_user === true) return t('common.system_user')
  else if (payload.solicitor === true) return t('common.solicitor')
  else return payload.u_name
}

const sortedTokens = computed(() =>
  auth.tokensList.value.toSorted((a, b) => {
    if (tokenSubtitle(a.payload) === tokenSubtitle(b.payload))
      return tokenTitle(a.payload).localeCompare(tokenTitle(b.payload), getIntlLocales())
    return (tokenSubtitle(a.payload) ?? '').localeCompare(tokenSubtitle(b.payload) ?? '', getIntlLocales())
  })
)

function isTokenOnboarded(payload?: AdaJwtClaims | null): boolean {
  if (isPreAuthOnboardingToken(payload)) return false
  if (!isUserToken(payload)) return true
  return payload.c_onb !== false
}

const activeTokens = computed(() => sortedTokens.value.filter((t) => isTokenOnboarded(t.payload)))
const inactiveTokens = computed(() => sortedTokens.value.filter((t) => !isTokenOnboarded(t.payload)))
</script>

<template>
  <v-menu
    v-if="
      auth.tokensList.value.length > 1 &&
      (!isUserToken(auth.selectedToken.value?.payload) || auth.selectedToken.value.payload.signed_in_by_u_id == null)
    "
    transition="slide-y-transition"
  >
    <template #activator="{ props, isActive }">
      <v-btn
        color="white"
        variant="outlined"
        v-bind="props"
      >
        <div class="text-left">
          <div class="font-weight-bold line-height-1">
            {{ tokenTitle(auth.selectedToken.value?.payload) }}
          </div>
          <div class="text-body-small line-height-0.75">
            {{ tokenSubtitle(auth.selectedToken.value?.payload) }}
          </div>
        </div>

        <template #append>
          <v-icon
            :icon="mdiChevronDown"
            class="chevron-rotate"
            :class="{ 'chevron-rotate--active': isActive }"
          />
        </template>
      </v-btn>
    </template>

    <v-list>
      <v-list-item
        v-for="item in activeTokens"
        :key="item.token"
        lines="one"
        :disabled="item.selected"
        @click="switchTo(item)"
      >
        <template #prepend>
          <v-avatar :color="textToBackgroundColor(tokenAvatarText(item.payload) ?? '')">
            {{ textInitials(tokenAvatarText(item.payload)) }}
          </v-avatar>
        </template>

        <v-list-item-title>
          {{ tokenTitle(item.payload) }}
        </v-list-item-title>
        <v-list-item-subtitle>
          {{ tokenSubtitle(item.payload) }}
        </v-list-item-subtitle>

        <template #append>
          <div
            v-if="item.selected"
            class="text-body-small ml-2"
          >
            {{ t('common.current') }}
            <v-chip
              v-if="isUserToken(item.payload) && item.payload.signed_in_by_u_id"
              :value="t('common.signed_in_as_other_user')"
              color="orange"
              size="small"
            />
          </div>
          <v-icon
            v-else
            :icon="mdiAccountSwitchOutline"
          />
        </template>
      </v-list-item>

      <template v-if="inactiveTokens.length > 0">
        <v-divider class="mt-3" />
        <v-list-subheader>{{ t('common.not_activated') }}</v-list-subheader>
        <v-list-item
          v-for="item in inactiveTokens"
          :key="item.token"
          lines="one"
          :disabled="item.selected"
          @click="switchTo(item)"
        >
          <template #prepend>
            <v-avatar :color="textToBackgroundColor(tokenAvatarText(item.payload) ?? '')">
              {{ textInitials(tokenAvatarText(item.payload)) }}
            </v-avatar>
          </template>

          <v-list-item-title>
            {{ tokenTitle(item.payload) }}
          </v-list-item-title>
          <v-list-item-subtitle>
            {{ tokenSubtitle(item.payload) }}
          </v-list-item-subtitle>

          <template #append>
            <div
              v-if="item.selected"
              class="text-body-small ml-2"
            >
              {{ t('common.current') }}
            </div>
            <v-icon
              v-else
              :icon="mdiAccountSwitchOutline"
            />
          </template>
        </v-list-item>
      </template>
    </v-list>
  </v-menu>
</template>

<style scoped>
.line-height-1 {
  line-height: 1;
}

.line-height-0\.75 {
  line-height: 0.75;
}
</style>
